import React, { useState, useEffect } from 'react';
import Spinner from './Spinner';

/**
 * Get the display value from an option (supports both string and object formats).
 */
function getOptionValue(option) {
  if (typeof option === 'string') return option;
  return option?.value || '';
}

function ExternalParticipantModal({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  mode = 'add', // 'add' or 'edit'
  participant = null, // For edit mode
  formSchema = null // Optional registration form schema
}) {
  const [formData, setFormData] = useState({
    external_name: '',
    external_phone: '',
    external_email: '',
    external_notes: '',
    form_responses: {}
  });
  const [errors, setErrors] = useState({});

  // Reset form when modal opens or participant changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && participant) {
        setFormData({
          external_name: participant.external_name || '',
          external_phone: participant.external_phone || '',
 external_email: participant.external_email || '',
           external_notes: participant.external_notes || '',
          form_responses: participant.form_responses || {}
        });
      } else {
        setFormData({
          external_name: '',
          external_phone: '',
external_email: '',
           external_notes: '',
          form_responses: {}
        });
      }
      setErrors({});
    }
  }, [isOpen, mode, participant]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handlePhoneChange = (value) => {
    const filtered = value.replace(/[^0-9+]/g, '');
    setFormData(prev => ({ ...prev, external_phone: filtered }));
    if (errors.external_phone) {
      setErrors(prev => ({ ...prev, external_phone: null }));
    }
  };

  const handleFormResponseChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      form_responses: { ...prev.form_responses, [fieldId]: value }
    }));
  };

  const handleCheckboxResponseChange = (fieldId, option, checked) => {
    setFormData(prev => {
      const current = prev.form_responses[fieldId] || [];
      const updated = checked
        ? [...current, option]
        : current.filter(o => o !== option);
      return {
        ...prev,
        form_responses: { ...prev.form_responses, [fieldId]: updated }
      };
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.external_name.trim()) {
      newErrors.external_name = 'Il nome completo è obbligatorio';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      // Only include form_responses if there's a form and responses exist
      const submitData = {
        external_name: formData.external_name.trim(),
        external_phone: formData.external_phone.trim() || null,
        external_email: formData.external_email.trim() || null,
        external_notes: formData.external_notes.trim() || null,
      };

      // Only include form_responses if schema exists and responses have values
      if (formSchema && Object.keys(formData.form_responses).length > 0) {
        submitData.form_responses = formData.form_responses;
      }

      onSubmit(submitData);
    }
  };

  const renderFormField = (field) => {
    const value = formData.form_responses[field.id];

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleFormResponseChange(field.id, e.target.value)}
            placeholder={field.placeholder || ''}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleFormResponseChange(field.id, e.target.value)}
            placeholder={field.placeholder || ''}
            rows={3}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300"
          />
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {(field.options || []).map((option, idx) => {
              const optionValue = getOptionValue(option);
              return (
                <label key={idx} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={`form-${field.id}`}
                    value={optionValue}
                    checked={value === optionValue}
                    onChange={(e) => handleFormResponseChange(field.id, e.target.value)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">{optionValue}</span>
                </label>
              );
            })}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            {(field.options || []).map((option, idx) => {
              const optionValue = getOptionValue(option);
              return (
                <label key={idx} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(value || []).includes(optionValue)}
                    onChange={(e) => handleCheckboxResponseChange(field.id, optionValue, e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-700">{optionValue}</span>
                </label>
              );
            })}
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {mode === 'edit' ? 'Modifica Partecipante Esterno' : 'Aggiungi Partecipante Esterno'}
            </h2>
            <p className="text-gray-600 text-sm">
              {mode === 'edit'
                ? 'Modifica i dati del partecipante'
                : 'Aggiungi una persona che si è iscritta al di fuori dell\'app'}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content - Scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Completo <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.external_name}
                onChange={(e) => handleChange('external_name', e.target.value)}
                placeholder="es. Mario Rossi"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.external_name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.external_name && (
                <p className="mt-1 text-sm text-red-600">{errors.external_name}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefono
              </label>
             <input
                 type="tel"
                 value={formData.external_phone}
                 onChange={(e) => handlePhoneChange(e.target.value)}
                 placeholder="es. 3331234567"
                 className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
               />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.external_email}
                onChange={(e) => handleChange('external_email', e.target.value)}
                placeholder="es. mario.rossi@email.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note
              </label>
              <textarea
                value={formData.external_notes}
                onChange={(e) => handleChange('external_notes', e.target.value)}
                placeholder="es. Deve noleggiare attrezzatura, è allergico alla lattuga..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Registration Form Fields (if schema exists) */}
            {formSchema && formSchema.length > 0 && (
              <div className="pt-4 border-t">
                <h3 className="text-lg font-medium text-gray-800 mb-4">
                  Risposte al Modulo di Iscrizione
                  <span className="text-sm font-normal text-gray-500 ml-2">(opzionale)</span>
                </h3>
                <div className="space-y-4">
                  {formSchema.map((field) => (
                    <div key={field.id}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label}
                        {field.required && <span className="text-gray-400 ml-1">(richiesto per iscrizioni via app)</span>}
                      </label>
                      {renderFormField(field)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 rounded-b-lg flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-white rounded-lg transition font-medium disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: loading ? '#93C5FD' : '#0E60C6' }}
              onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = '#0B4FA3')}
              onMouseLeave={(e) => !loading && (e.currentTarget.style.backgroundColor = '#0E60C6')}
            >
              {loading && <Spinner size="sm" color="white" />}
              {mode === 'edit'
                ? (loading ? 'Salvataggio...' : 'Salva Modifiche')
                : (loading ? 'Aggiunta...' : 'Aggiungi Partecipante')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ExternalParticipantModal;
